<!--
  This component should be used as a base for all successfully updated screens (eg. Payment details successfully updated, Member details successfully updated) with premium impact/no impact and with related pages/articles at the end. It has slots for all corresponding sections that can be passed in (find names below) and defaults for some sections. Aim should be to use this component on all screens with similar structure and keep updating below as required without breaking changes and keeping in mind Vue 2 best practices.
-->

<template>
  <div id="main-content">
    <!-- Title slot -->
    <SuccessBanner>
      <template #header>
        <slot name="header">
          <h1>Details successfully updated</h1>
        </slot>
      </template>
      <slot name="headerInfoText">
        <div v-if="showContents" class="top-content fr-view">
          <div v-html="refData.premiumImpactInformationText.informationText" />
        </div>
      </slot>
    </SuccessBanner>
    <!-- Info text below title slot -->
    <div class="mid-content" v-if="showMidContent">
      <!-- Slot for "Your premium information" title -->
      <slot name="tableHeader">
        <h2>Your premium information</h2>
      </slot>
      <ProductSummaryCard v-if="((adjustmentPrices && !Object.values(adjustmentPrices).every(value => value === null)) || premiumData) && !$slots.productPremiumContent">
        <template #left>
          <slot name="tableContent">
            <table class="borderless compact" v-if="adjustmentPrices !== undefined">
              <tbody>
                <tr v-if="adjustmentPrices.oldPremium && adjustmentPrices.oldFrequency">
                  <th>Previous premium</th>
                  <td>{{ formatToDisplayCurrencyPeriod(adjustmentPrices.oldPremium, adjustmentPrices.oldFrequency) }}</td>
                </tr>
                <tr v-if="adjustmentPrices.newPremium && adjustmentPrices.newFrequency">
                  <th>New ongoing premium amount</th>
                  <td>{{ formatToDisplayCurrencyPeriod(adjustmentPrices.newPremium, adjustmentPrices.newFrequency) }}</td>
                </tr>
                <tr v-if="adjustmentPrices.newExcess">
                  <th>New excess</th>
                  <td>{{ convertToCurrencyString(adjustmentPrices.newExcess) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else-if="premiumData !== undefined" class="table-info">
              <p>${{ premiumData.newPremium }} / {{ premiumData.currentPayFrequency }}</p>
              <p v-if="premiumData.rebateTierValue && premiumData.rebateTierName">{{ premiumData.rebateTierValue }}: {{ premiumData.rebateTierName }}</p>
              <p v-else>No rebate tier</p>
              <p v-if="premiumData.nextContributionDate">
                Effective from {{ format(new Date(premiumData.nextContributionDate), 'dd MMMM yyyy') }}
                <br />
                and applied to the first premium payment due on or after this date
              </p>
              <p v-if="premiumData.futureContributionDate">Next payment due {{ format(new Date(premiumData.futureContributionDate), 'dd MMMM yyyy') }}</p>
            </div>
            <div class="future-dated">
              <p>
                The next payment amount shown below does not consider any changes that may occur on the policy in the next payment period such as loading or discount removal. If
                any changes do occur between the current date and the next payment date this amount may vary.
              </p>
            </div>
          </slot>
        </template>
        <template #right>
          <h4 class="header">New ongoing premium</h4>
          <p class="crimson bold no-wrap">
            <template v-if="adjustmentPrices !== undefined">
              <span class="font-large">${{ adjustmentPrices.oldPremium }}</span>
              / {{ mapPaymentFrequencyFromRecurringToSingleton(adjustmentPrices.oldFrequency) }}
            </template>
            <template v-else-if="premiumData !== undefined">
              <span clas="font-large">${{ premiumData.newPremium }}</span>
              / {{ premiumData.currentPayFrequency }}
            </template>
          </p>
        </template>
      </ProductSummaryCard>
      <slot name="productPremiumContent"></slot>

      <!-- Slot for call-out box -->
      <slot name="callOutSlot">
        <div v-if="showCallOutText" :class="showCallOutText ? 'call-out call-to-action' : ''">
          <div v-html="refData.premiumImpactInformationText.callOutText" />
        </div>
      </slot>
      <div class="fr-view" :class="!showCallOutText && !isCallOutSlotPassedIn ? 'no-call-out' : ''">
        <!-- Slot for payment timeline -->
        <slot name="premiumAdjustmentTimeline"></slot>
      </div>
    </div>

    <!-- Related articles/pages -->
    <slot name="relatedArticles"></slot>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { convertToCurrencyString } from '@general/js/utils';
import { formatToDisplayCurrencyPeriod, mapPaymentFrequencyFromRecurringToSingleton } from '@/utils/DisplayUtils';
import SuccessBanner from './SuccessBanner.vue';
import ProductSummaryCard from './ProductSummaryCard.vue';

export default {
  name: 'PremiumImpactResult',

  components: {
    SuccessBanner,
    ProductSummaryCard,
  },

  props: {
    refData: {
      type: Object,
    },
    /**
     * Contains the adjustment prices of premium and new excess (if changed)
     * Will have 5 properties oldPremium, newPremium, oldFrequency, newFrequency and newExcess
     */
    adjustmentPrices: {
      type: Object,
    },

    /**
     * Will have 4 properties currentPremium, currentPayFrequency, rebateTierValue and rebateTierName
     */
    premiumData: {
      type: Object,
    },

    /**
     * Boolean to indicate whether to show the content in the middle (Your premium information(& payment timeline) AND callout box)
     */
    showMidContent: {
      type: Boolean,
      default: true, // default true so that it doesn't break any existing screens.
    },

    /**
     * Boolean to indicate to show premium information content only
     */
    showPremiumInformation: {
      type: Boolean,
      default: true, // default true so that it doesn't break any existing screens.
    },
  },

  data() {
    return {
      informationText: '',
    };
  },

  computed: {
    showContents() {
      if (this.refData && this.refData.premiumImpactInformationText) {
        if (this.refData.premiumImpactInformationText.informationText) {
          return true;
        }
      }
      return false;
    },
    showCallOutText() {
      if (this.refData?.premiumImpactInformationText) {
        if (this.refData.premiumImpactInformationText.callOutText) {
          return true;
        }
      }
      return false;
    },
    isCallOutSlotPassedIn() {
      // show callout if callOutSlot is passed in
      return this.$slots.callOutSlot?.length;
    },
  },

  methods: {
    convertToCurrencyString,
    formatToDisplayCurrencyPeriod,
    format,
    mapPaymentFrequencyFromRecurringToSingleton,
  },
};
</script>

<style lang="scss" scoped>
.hidden {
  /** this class hides <h2> as per design but due to accessibility, <h2> is required, to pass accessibility, and screen reader, change opacity hence it is hidden and use as spacing between top content and quick links */
  opacity: 0.01; // use opacity instead of display none or visibility hidden to pass accessibility
  margin-bottom: 0;
  height: spacer(5); // for spacing between top content and quick links, which is 70px as per design suggested (20px is coming from <QuickLinks> style)
  overflow: hidden;
}

.top-content {
  margin-bottom: 2rem;
  & > * {
    margin-bottom: 0;
  }
}
.mid-content {
  .no-call-out {
    margin-top: 20px;

    @include mobile {
      margin-top: 0;
    }
  }

  .table-info {
    font-weight: $BOLD;
  }

  @include mobile {
    --NUMBER-OF-COLUMS: 1;
    gap: spacer(2);
  }

  .table-info-head {
    font-weight: $BOLD;
    font-size: font-size(2);
    color: $CRIMSON;
    margin-top: 0;

    @include mobile {
      font-size: font-size(1);
    }
  }

  table {
    width: 100%;
    border: none;

    tbody {
      width: inherit;
      display: inherit;

      th {
        padding-left: 0;
      }

      tr {
        width: inherit;
        display: flex;
        justify-content: space-between;
      }
    }
  }

  .no-wrap {
    white-space: nowrap;
  }

  :deep(section.focusable) {
    margin-bottom: 0;

    & > .separator-wrapper {
      grid-template-columns: 1fr;
      gap: 5px 0;
    }

    & > .separator-content {
      & > h2 {
        grid-template-columns: 1fr;
      }

      @include desktop {
        padding-bottom: rem(5);
      }
    }
  }

  .call-out {
    margin-bottom: 0;
    align-items: start;
    height: fit-content;
    text-align: left;
  }

  & > .fr-view {
    @include tablet-and-above {
      --NUMBER-OF-COLUMS: 2;
    }
    display: grid;
    grid-template-columns: repeat(var(--NUMBER-OF-COLUMS), 1fr);
    gap: 0 spacer(3);
  }
}
#premium-info {
  display: block;
}
</style>
